// src/pages/Macroeconomia.jsx
import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  GlobalStyles,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { jsPDF } from "jspdf";
import BloqueMacroeconomicoRow from "../components/BloqueMacroeconomicoRow";

// Paleta
const PALETA = {
  fesRed: "#A6110F",
  govBlue: "#0B3D91",
  navy: "#082B73",
  sky: "#1976d2",
  neutralBg: "#F5F6F8",
  neutralBorder: "#E4E6EB",
  textPrimary: "#1E1E1E",
  textSecondary: "#5A5D63",
};

const COLORS_BY_BLOCK = {
  PIB: "#0B3D91",
  IMACEC: "#A6110F",
  IPC: "#F39C12",
  "Deuda Pública": "#6C5CE7",
  "Inserción Internacional": "#00A4A6",
  TPM: "#2E7D32",
};

// === BLOQUES EXACTOS ===
const BLOQUES = [
  {
    key: "PIB",
    titulo: "Producto Interno Bruto (PIB)",
    color: COLORS_BY_BLOCK.PIB,
    textoIntro:
      "La macroeconomía define condiciones concretas de vida: empleo, salarios, precios y expectativas. El PIB mide el valor total de los bienes y servicios producidos en el país.",
    explicacionSimple:
      "PIB = todo lo que produce Chile. Si crece lento o cae, se enfría el empleo; si crece rápido, puede mejorar la actividad.",
    impactoPersona: "PIB estancado = menos inversión y presión sobre salarios.",
    descripcionGrafico:
      "El PIB muestra el crecimiento o contracción de la economía.",
    series: [
      { id: "F032.PIB.FLU.R.CLP.2018.Z.Z.1.M", nombre: "PIB real" },
      { id: "F032.PIB.FLU.N.CLP.EP18.Z.Z.0.T", nombre: "PIB nominal" },
      { id: "F032.PIB.PP.Z.USD.2018.Z.Z.0.A", nombre: "PIB per cápita" },
    ],
  },

  {
    key: "IMACEC",
    titulo: "Índice Mensual de Actividad Económica (IMACEC)",
    color: COLORS_BY_BLOCK.IMACEC,
    textoIntro:
      "El IMACEC permite ver el pulso mensual de la actividad económica y anticipar cómo viene el PIB.",
    explicacionSimple:
      "El IMACEC es como un ‘mini PIB’ mensual que muestra el pulso inmediato de la economía.",
    impactoPersona:
      "Si cae por varios meses, normalmente baja la demanda por trabajo.",
    descripcionGrafico: "Mide la actividad mensual de la economía chilena.",
    series: [
      { id: "F032.IMC.IND.Z.Z.EP18.Z.Z.1.M", nombre: "Imacec desestacionalizado" },
      { id: "F032.IMC.IND.Z.Z.EP18.Z.Z.0.M", nombre: "Imacec empalmado" },
    ],
  },

  {
    key: "IPC",
    titulo: "Índice de Precios al Consumidor (IPC)",
    color: COLORS_BY_BLOCK.IPC,
    textoIntro:
      "El IPC mide cuánto suben los precios. La inflación afecta directamente el poder de compra.",
    explicacionSimple:
      "El IPC es la inflación. Mide cuánto suben alimentos, transporte y servicios.",
    impactoPersona:
      "Inflación alta = tu sueldo rinde menos; suben los créditos por las tasas.",
    descripcionGrafico: "Mide la variación de precios en la economía.",
    series: [
      { id: "F074.IPC.V12.Z.EP23.C.M", nombre: "IPC variación anual" },
      { id: "G073.IPCSV.V12.2023.M", nombre: "IPC sin volátiles" },
      { id: "G073.IPCV.V12.2023.M", nombre: "IPC volátiles" },
    ],
  },

  {
    key: "Deuda Pública",
    titulo: "Deuda Pública",
    color: COLORS_BY_BLOCK["Deuda Pública"],
    textoIntro:
      "La deuda pública muestra cuánto debe el Estado para sostener gasto social, inversión o enfrentar crisis.",
    explicacionSimple:
      "No es automáticamente buena o mala: depende para qué se usa.",
    impactoPersona:
      "Deuda usada sólo para emergencias deja poco margen para inversión social.",
    descripcionGrafico: "Indica el nivel de endeudamiento del Estado.",
    series: [
      { id: "F051.D7.PPB.C.Z.Z.T", nombre: "Deuda bruta Gobierno Central" },
      { id: "F051.E7.PPB.H.Z.Z.T", nombre: "Deuda bruta Banco Central" },
    ],
  },

  {
    key: "Inserción Internacional",
    titulo: "Inserción Internacional",
    color: COLORS_BY_BLOCK["Inserción Internacional"],
    textoIntro:
      "Chile es una economía abierta, dependiente de importaciones de energía y tecnología.",
    explicacionSimple:
      "Exportaciones = lo que vendemos; importaciones = lo que compramos.",
    impactoPersona:
      "Si sube el dólar, suben alimentos, combustibles y servicios.",
    descripcionGrafico:
      "Muestra comercio internacional: exportaciones, importaciones y balanza.",
    series: [
      { id: "F068.B1.FLU.Z.0.C.N.Z.Z.Z.Z.6.3.T", nombre: "Exportaciones FOB" },
      { id: "F068.B1.FLU.Z.0.M.N.0.Z.Z.Z.6.3.T", nombre: "Importaciones CIF" },
      { id: "F068.B1.VAR.T0.0.S.N.Z.Z.Z.Z.6.0.M", nombre: "Balanza comercial" },
    ],
  },

  {
    key: "TPM",
    titulo: "Tasa de Política Monetaria (TPM)",
    color: COLORS_BY_BLOCK.TPM,
    textoIntro:
      "La TPM define el costo del crédito en Chile. Cambios bruscos afectan inflación, empleo e inversión.",
    explicacionSimple:
      "Es la tasa base que usan los bancos para fijar sus propias tasas.",
    impactoPersona:
      "TPM alta = créditos caros (consumo, hipotecarios, pyme).",
    descripcionGrafico:
      "Mide la tasa de interés determinada por el Banco Central.",
    series: [
      { id: "F022.TPM.TIN.D001.NO.Z.D", nombre: "TPM diaria" },
      { id: "F022.TPM.TIN.M001.NO.Z.M", nombre: "TPM mensual" },
    ],
  },
];

// ======================================================
//          NUEVO PDF QUE NO VIENE EN BLANCO
// ======================================================
const generarPDF = () => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  const marginX = 40;
  const maxWidth = 515;
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = 60;

  const salto = (extra = 0) => {
    if (y + extra > pageHeight - 70) {
      doc.addPage();
      y = 60;
    }
  };

  // === Portada ===
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Informe Macroeconómico", marginX, y);
  y += 30;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const intro = doc.splitTextToSize(
    "Este informe resume los principales indicadores macroeconómicos de Chile en un lenguaje claro: PIB, IMACEC, precios (IPC), deuda pública, inserción internacional y tasa de interés (TPM).",
    maxWidth
  );
  doc.text(intro, marginX, y);
  y += intro.length * 14 + 20;

  // === BLOQUES ===
  BLOQUES.forEach((b, idx) => {
    salto(140);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(b.titulo, marginX, y);
    y += 20;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);

    const textos = [
      b.textoIntro,
      "En simple: " + b.explicacionSimple,
      "¿Por qué importa? " + b.impactoPersona,
      "Qué muestra el gráfico: " + b.descripcionGrafico,
    ];

    textos.forEach((t) => {
      const lines = doc.splitTextToSize(t, maxWidth);
      doc.text(lines, marginX, y);
      y += lines.length * 14 + 8;
    });

    // Series
    if (b.series?.length) {
      const lista = b.series.map(
        (s) => `• ${s.nombre} (id BC: ${s.id})`
      );
      const l2 = doc.splitTextToSize(lista.join("\n"), maxWidth);
      doc.text(l2, marginX, y);
      y += l2.length * 14 + 14;
    }

    if (idx < BLOQUES.length - 1) {
      salto(30);
      doc.setDrawColor(200);
      doc.line(marginX, y, marginX + maxWidth, y);
      y += 20;
    }
  });

  // Footer
  salto(40);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  const footer = doc.splitTextToSize(
    "Fuente: Banco Central de Chile. Documento generado por Nodo XXI — Datos para la Democracia.",
    maxWidth
  );
  doc.text(footer, marginX, y);

  doc.save("reporte_macroeconomia.pdf");
};

// ======================================================
//                      COMPONENTE
// ======================================================
const Macroeconomia = () => {
  const [activeId, setActiveId] = useState("");
  const sectionRefs = useRef({});

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    setActiveId(id);

    const y =
      el.getBoundingClientRect().top + window.scrollY - 84;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <Box sx={{ width: "100%", overflowX: "clip" }}>
      <GlobalStyles
        styles={{
          "html, body, #root": { maxWidth: "100%", overflowX: "clip" },
          ".page-bg, .page-overlay": { overflowX: "clip" },
          "svg, canvas, img, video": { maxWidth: "100%" },
        }}
      />

      <Box sx={{ display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
        gap: { xs: 0, md: 3 },
        p: { xs: 2, md: 4 }
      }}>
        
        {/* === Sidebar === */}
        <Box sx={{
          position: { md: "sticky" },
          top: { md: 90 },
          borderRight: { md: `1px solid ${PALETA.neutralBorder}` },
          pr: { md: 2 }
        }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Indicadores
          </Typography>

          <List dense>
            {BLOQUES.map((b) => {
              const id = b.titulo.toLowerCase().replace(/[^\w]+/g, "-");
              return (
                <ListItemButton
                  key={id}
                  onClick={() => scrollTo(id)}
                  selected={activeId === id}
                >
                  <ListItemText primary={b.titulo} />
                </ListItemButton>
              );
            })}
          </List>

          <Divider sx={{ my: 2 }} />

          <Button
            fullWidth
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={generarPDF}
            sx={{ py: 1.25, borderRadius: 2, fontWeight: 700,
              backgroundColor: PALETA.govBlue,
              "&:hover": { backgroundColor: "#072a66" }
            }}
          >
            Descargar PDF
          </Button>
        </Box>

        {/* === Contenido principal === */}
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
            Macroeconomía
          </Typography>

          <Typography sx={{ color: PALETA.textSecondary, mb: 6 }}>
            Esta sección resume producción, actividad mensual, inflación,
            deuda pública, comercio exterior y tasas de interés.
          </Typography>

          {BLOQUES.map((b) => {
            const id = b.titulo.toLowerCase().replace(/[^\w]+/g, "-");
            return (
              <Box
                key={id}
                id={id}
                ref={(el) => (sectionRefs.current[id] = el)}
                sx={{ scrollMarginTop: "100px", mb: 6 }}
              >
                <BloqueMacroeconomicoRow
                  titulo={b.titulo}
                  color={b.color}
                  textoIntro={b.textoIntro}
                  descripcionGrafico={b.descripcionGrafico}
                  series={b.series}
                  paleta={PALETA}
                />
                <Divider sx={{ my: 4 }} />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default Macroeconomia;
